/**
 * Optimized JSON-Based Product Color Manager - Progressive Loading
 * 
 * Handles color switching for split products using optimized JSON mapping data.
 * Features:
 * 
 * PERFORMANCE OPTIMIZATION:
 * - Reduced initial payload - only essential data for current product
 * - Progressive image loading - 1st image immediately, others by priority
 * - Liquid content untouched until user interaction
 * - Zero network requests - all data pre-loaded efficiently
 * 
 * PROGRESSIVE LOADING STRATEGY:
 * - Page load: Only 1st image for current product (already done by Liquid)
 * - 300ms delay: Load 1st image for each color variant
 * - Priority-based: Load remaining images by importance
 * 
 * RELIABILITY:
 * - No network failures
 * - No parsing errors
 * - Consistent data structure
 * - Immediate UI updates
 */

(function() {
  'use strict';
  const BUTTON_STATES = {
	DEFAULT: 'DEFAULT',
    SELECT_SIZE: 'SELECT_SIZE',
    ADD_TO_CART: 'ADD_TO_CART',
    PRE_ORDER: 'PRE_ORDER',
    NOTIFY_ME: 'NOTIFY_ME'
  };
  class SplitProductManager {
    constructor() {
      this.currentColor = null;
      this.currentSize = null;
      this.userHasSelectedSize = false; // Track if user explicitly selected a size
      this.isLoading = false;
      this.variantMapping = null;
      this.updateColorNamesTimeout = null;
	  this.buttonState = BUTTON_STATES.SELECT_SIZE;

      this.init();
    }
    
    init() {
      // Initialize the split product manager
      console.log('🌐 INIT - Current URL:', window.location.href);
      this.waitForVariantMapping().then(() => {
        this.setupEventListeners();
        this.initializeFromURL();

        // Set up sticky button click listener on initial load
        this.setupStickyButtonScrollListener();

        // Ensure images are properly displayed for current color
        setTimeout(() => {
          if (this.currentColor) {
            this.hideAllImages();
            this.showImagesForColor(this.currentColor);
          }
        }, 100);

        // Update color names in UI after initialization
        setTimeout(() => {
          this.updateColorNamesInUI();
        }, 200);
      });
    }
    
    waitForVariantMapping() {
      return new Promise((resolve) => {
        const checkMapping = () => {
          if (window.variantMapping) {
            this.variantMapping = window.variantMapping;
            ////console.log('✅ Variant mapping loaded:', Object.keys(this.variantMapping).length, 'colors');
            console.debug('📄 VARIANT MAPPING:', this.variantMapping);
            
            resolve(); // Resolve immediately since we have Liquid image data
          } else {
            setTimeout(checkMapping, 100);
          }
        };
        checkMapping();
      });
    }
    

    

    

    
    getImageSizesFromPage() {
      // Detect actual image sizes used on the page
      const images = document.querySelectorAll('img[src*="products"], img[src*="cdn.shopify"]');
      const sizes = {
        main: '800', // default
        srcset: ['400', '800', '1200'], // default
        queryParams: {} // to preserve
      };
      
      if (images.length > 0) {
        const firstImage = images[0];
        const src = firstImage.src;
        const srcset = firstImage.srcset;
        
        // Extract width from current src
        try {
          const url = new URL(src);
          const width = url.searchParams.get('width');
          if (width) {
            sizes.main = width;
          }
          
          // Extract query parameters to preserve
          url.searchParams.forEach((value, key) => {
            if (key !== 'width') {
              sizes.queryParams[key] = value;
            }
          });
        } catch (e) {
          console.warn('Could not parse image URL:', e);
        }
        
        // Extract srcset sizes
        if (srcset) {
          const srcsetSizes = [];
          srcset.split(', ').forEach(part => {
            const [url, descriptor] = part.split(' ');
            if (descriptor) {
              const width = descriptor.replace('w', '');
              if (!isNaN(width)) {
                srcsetSizes.push(width);
              }
            }
          });
          if (srcsetSizes.length > 0) {
            sizes.srcset = srcsetSizes;
          }
        }
      }
      
      ////console.log('📏 Detected image sizes:', sizes);
      return sizes;
    }
    

    

    
    setupEventListeners() {
      // Color swatch clicks - prevent navigation and use JSON mapping
      document.addEventListener('click', (e) => {
        if (e.target.classList.contains('ajax-color-swatch') || e.target.closest('.ajax-color-swatch')) {
          e.preventDefault();
          e.stopPropagation();
          
          const swatch = e.target.classList.contains('ajax-color-swatch') ? e.target : e.target.closest('.ajax-color-swatch');
          const color = swatch.getAttribute('data-color');
          
          if (color && this.variantMapping[color]) {
            ////console.log(`🎨 Color swatch clicked: ${color} - preventing navigation`);
            this.switchColor(color);
          }
        }
      }, true);
      
      // Size option clicks - handle radio buttons with variant IDs (only for product variations)
      document.addEventListener('change', (e) => {
        if (e.target.name && e.target.name.includes('option2') && e.target.type === 'radio') {
          // Only intercept for product variations
          if (document.querySelector('.product.product-variations')) {
            e.preventDefault();
            e.stopPropagation();
            const variantId = e.target.value;
            const size = e.target.getAttribute('data-size');
            console.log('🎯 Size clicked (change event):', size, 'Variant ID:', variantId);
            console.log('🎯 Current product URL:', window.location.pathname);
            console.log('🎯 Available variants for current color:', this.currentColor, this.variantMapping[this.currentColor]);
            this.switchSizeByVariantId(variantId);
          }
        }
      });
      
      // Listen for clicks on size labels - prevent default behavior and use our logic (only for product variations)
      document.addEventListener('click', (e) => {
        if (e.target.tagName === 'LABEL' && e.target.getAttribute('for')) {
          const inputId = e.target.getAttribute('for');
          const input = document.getElementById(inputId);
          if (input && input.name && input.name.includes('option2') && input.type === 'radio') {
            // Only intercept for product variations
            if (document.querySelector('.product.product-variations')) {
              e.preventDefault();
              e.stopPropagation();
              const variantId = input.value;
              const size = input.getAttribute('data-size');
              console.log('🎯 Size clicked (click event):', size, 'Variant ID:', variantId);
              this.switchSizeByVariantId(variantId);
            }
          }
        }
      });
    }

    setupStickyButtonScrollListener() {
      // Set up click listener for sticky "Select size" button to scroll to buy-buttons
      const stickyButton = document.querySelector('.product-sticky-bar button');
      if (!stickyButton) {
        console.log('⚠️ Sticky button not found during initialization');
        return;
      }

      console.log('🔘 Setting up sticky button scroll listener on initialization');

      // Clone to remove any existing listeners
      const newButton = stickyButton.cloneNode(true);
      stickyButton.parentNode.replaceChild(newButton, stickyButton);

      // Add the click listener
      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('📏 Sticky button clicked - calculating scroll...');

        // Find the buy-buttons section
        const buyButtons = document.querySelector('[data-block-id="buy_buttons"] .buy-buttons');
        if (!buyButtons) {
          console.warn('⚠️ Buy-buttons section not found');
          return;
        }

        // Get the document-absolute position of buy-buttons
        const buyButtonsRect = buyButtons.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const buttonHeight = buyButtonsRect.height;

        // Current scroll position in document
        const currentScrollY = window.scrollY;

        // Document position of buy-buttons top
        const buyButtonsDocumentTop = currentScrollY + buyButtonsRect.top;

        // Where we want the buy-buttons bottom to be: 50px from viewport bottom
        // This means: viewport height - button height - 50px = where button top should be in viewport
        const targetViewportTop = viewportHeight - buttonHeight - 50;

        // So in document coordinates: we want buy-buttons top at (scrollY + targetViewportTop)
        // Solving: scrollY_new = buyButtonsDocumentTop - targetViewportTop
        const targetScrollY = buyButtonsDocumentTop - targetViewportTop;

        console.log(`📏 Scroll calculation: currentScrollY=${currentScrollY}, buyButtonsDocumentTop=${buyButtonsDocumentTop}, viewportHeight=${viewportHeight}, buttonHeight=${buttonHeight}, targetViewportTop=${targetViewportTop}, targetScrollY=${targetScrollY}`);

        // Scroll to the calculated position with smooth animation
        window.scrollTo({
          top: targetScrollY,
          behavior: 'smooth'
        });
        console.log('✅ Scrolling to buy-buttons (target: 50px from bottom)');
      });
    }

    initializeFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');
      
      if (variantId && variantId !== 'undefined') {
        // User had selected a size (variant ID in URL) - restore the state
        for (const [color, sizes] of Object.entries(this.variantMapping)) {
          for (const [size, data] of Object.entries(sizes)) {
            if (data.variant_id.toString() === variantId) {
              this.currentColor = color;
              this.currentSize = size;
              this.userHasSelectedSize = true; // User had selected a size (variant ID in URL)
              this.updateColorUI(color);
              this.selectSizeInUI(size, data.variant_id);
              this.updateSizeAvailabilityUI();
              /////console.log(`🎯 Restored state from URL: ${color} / ${size}`);
              return;
            }
          }
        }
      }
      
      // No variant ID in URL - just determine current color without modifying UI
      // Leave Liquid-rendered content untouched - no flicker/movement
      const currentProductId = this.getCurrentProductId();
      console.log('🔍 initializeFromURL() - currentProductId:', currentProductId);
      
      // Debug: Show all unique product IDs in the mapping
      const uniqueProductIds = new Set();
      for (const [color, sizes] of Object.entries(this.variantMapping)) {
        for (const [size, data] of Object.entries(sizes)) {
          uniqueProductIds.add(data.product_id);
        }
      }
      console.log('🔍 All product IDs in variantMapping:', Array.from(uniqueProductIds));
      
      if (currentProductId) {
        for (const [color, sizes] of Object.entries(this.variantMapping)) {
          for (const [size, data] of Object.entries(sizes)) {
            console.log('🔍 Checking color:', color, 'size:', size, 'product_id:', data.product_id, 'matches?', data.product_id === currentProductId);
            if (data.product_id === currentProductId) {
              this.currentColor = color;
              // Set initial size to first available size for this color
              const availableSizes = Object.keys(sizes);
              if (availableSizes.length > 0) {
                this.currentSize = availableSizes[0];
                console.log('🔍 Set current color to:', color, 'and initial size to:', this.currentSize);

                // If this color has only one size, automatically consider it selected
                if (availableSizes.length === 1) {
                  this.userHasSelectedSize = true;
                  console.log(`🎯 Auto-selected single size during initialization: ${this.currentSize} for color: ${color}`);
                }
              } else {
                console.log('🔍 Set current color to:', color, 'but no sizes available');
              }
              // DON'T call updateColorUI() or updateSizeAvailabilityUI() - leave Liquid content as-is
              ////console.log(`🎯 Initialized with current product color: ${color} (no UI changes)`);
              return;
            }
          }
        }
      }
      
      // Fallback to first color if current product not found
      
    }
    
    getCurrentProductId() {
      console.log('🔍 getCurrentProductId() - Current URL:', window.location.pathname);
      
      // Try to get product ID from various sources
      const productId = document.querySelector('.product.product-variations[data-product-id]')?.dataset.productId;
      if (productId) {
        console.log('🔍 Found product ID from data attribute:', productId);
        return parseInt(productId);
      }
      
      // Try to get from variant data script
      const variantScript = document.querySelector('script[data-variant]');
      if (variantScript) {
        try {
          const variantData = JSON.parse(variantScript.textContent);
          console.log('🔍 Found product ID from variant script:', variantData.product_id);
          return variantData.product_id;
        } catch (e) {
          console.warn('Could not parse variant data script:', e);
        }
      }
      
      // Try to get from URL path
      const pathMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
      if (pathMatch) {
        const handle = pathMatch[1];
        console.log('🔍 URL handle:', handle);
        // Find product by handle in variant mapping
        for (const [color, sizes] of Object.entries(this.variantMapping)) {
          for (const [size, data] of Object.entries(sizes)) {
            console.log('🔍 Checking:', color, size, 'product_url:', data.product_url, 'includes handle?', data.product_url.includes(handle));
            if (data.product_url.includes(handle)) {
              console.log('🔍 Found matching product ID:', data.product_id, 'for color:', color);
              return data.product_id;
            }
          }
        }
      }
      
      console.log('🔍 No product ID found');
      return null;
    }
    
    selectSizeInUI(size, variantId) {
      // 1. Remove all .selected .is-selected from all sizes
      const allSizeLabels = document.querySelectorAll('label.block-swatch[for*="option2"]');
      allSizeLabels.forEach(label => {
        label.classList.remove('selected', 'is-selected');
      });
      
      // 2. Uncheck all radio buttons
      const allSizeInputs = document.querySelectorAll('input[name*="option2"][type="radio"]');
      allSizeInputs.forEach(input => {
        input.checked = false;
      });
      
      // 3. Check the target input
      const targetInput = document.querySelector(`input[data-size="${size}"]`);
      if (targetInput) {
        targetInput.checked = true;
      }
      
      // 4. Add .selected to the label
      const targetLabel = document.querySelector(`label[for="${targetInput?.id}"]`);
      if (targetLabel) {
        targetLabel.classList.add('selected');
      }
      
      // Update current size
      this.currentSize = size;
    }
    
    updateSizeAvailabilityUI() {
      if (!this.currentColor || !this.variantMapping[this.currentColor]) return;
      
      const sizeLabels = document.querySelectorAll('label.block-swatch[for*="option2"]');
      sizeLabels.forEach(label => {
        const sizeText = label.textContent.trim();
        
        // Find if this size is available for current color
        const variantData = this.variantMapping[this.currentColor][sizeText];
        const isAvailable = variantData && variantData.available;
        
        if (isAvailable) {
          label.classList.remove('is-disabled');
        } else {
          label.classList.add('is-disabled');
        }
      });
    }
    
    async switchColor(color) {
      if (this.isLoading) return;
      
      console.log('🌐 BEFORE COLOR SWITCH - Current URL:', window.location.href);
      
      const startTime = performance.now();
      
      // Check if color is already selected
      if (this.currentColor === color) {
        const noChangeTime = performance.now() - startTime;
        //console.log(`⏭️ No color change needed (${color} already selected) - Time to detect: ${noChangeTime.toFixed(2)}ms`);
        return;
      }
      
//      //console.log(`🚀 Starting color switch to: ${color}`);
      
      this.isLoading = true;
      
      try {
        const setupStart = performance.now();
        
        // Get available sizes for the new color
        const sizes = this.variantMapping[color];
        const availableSizes = Object.keys(sizes);
        let targetSize = this.currentSize;
        let variantData = sizes[targetSize];

        // Check if this color has only one size available
        const hasOnlyOneSize = availableSizes.length === 1;

        // FIXED: Find the correct variant for new color + selected size
        if (this.userHasSelectedSize && this.currentSize) {
          // User has selected a size, find the variant for new color + current size
          variantData = this.findVariantForColorAndSize(color, this.currentSize);
          if (variantData) {
            targetSize = this.currentSize; // Keep the same size

          } else {
            // Fallback: use first available size for new color
            targetSize = availableSizes[0];
            variantData = sizes[targetSize];

          }
        } else if (!variantData && this.userHasSelectedSize) {
          // User had selected a size before, so auto-select first available size for new color
          targetSize = availableSizes[0];
          variantData = sizes[targetSize];
        } else if (!variantData && !this.userHasSelectedSize) {
          // User hasn't selected a size yet
          targetSize = availableSizes[0];
          variantData = sizes[targetSize];

          // If this color has only one size, automatically consider it selected
          if (hasOnlyOneSize) {
            this.currentSize = targetSize;
            this.userHasSelectedSize = true;
            console.log(`🎯 Auto-selected single size: ${targetSize} for color: ${color}`);
          } else {
            // Multiple sizes available - don't auto-select, keep it null to show "SELECT SIZE" button
            // Don't update this.currentSize - keep it null to show "SELECT SIZE" button
          }
        }
        
        const setupTime = performance.now() - setupStart;
        //console.log(`⚡ Setup time: ${setupTime.toFixed(2)}ms`);
        
        // Update current state
        this.currentColor = color;
        // Only update currentSize if user had previously selected a size
        if (this.userHasSelectedSize) {
          this.currentSize = targetSize;
        }
        // If user hasn't selected a size, keep this.currentSize as null
        
        // Update global state for test access
        window.currentColor = color;
        window.currentSize = targetSize;
        
        // Debug logging for test

        
        const urlStart = performance.now();
        
        // Navigate to the correct product URL for this color (REQUIRED for split product architecture)
        if (variantData.product_url) {
          let newUrl = variantData.product_url;
          
          // FIXED: Use the correct variant ID for new color + selected size
          if (this.userHasSelectedSize) {
            newUrl += `?variant=${variantData.variant_id}${window.location.search.replace(/[?&]variant=[^&]*/, '')}`;
    
          } else {
            // No size selected, just update product URL without variant ID
            newUrl += window.location.search.replace(/[?&]variant=[^&]*/, '');
    
          }
          
          window.history.replaceState({}, '', newUrl);
        } else {
          // Fallback: just update the variant ID (only if size selected)
          if (this.userHasSelectedSize) {
            this.updateURL(variantData.variant_id);
          }
        }
        
        const urlTime = performance.now() - urlStart;
        ////console.log(`🔗 URL update time: ${urlTime.toFixed(2)}ms`);
        
        const uiStart = performance.now();
        
        // Update UI immediately for responsiveness
        this.updateColorUI(color);
        this.updateSizeAvailabilityUI();
        
        const uiTime = performance.now() - uiStart;
        ////console.log(`🎨 UI update time: ${uiTime.toFixed(2)}ms`);
        
        const dataStart = performance.now();
        
        // UPDATE ALL DATA FROM JSON MAPPING - NO AJAX NEEDED
        this.updateAllDataFromMapping(variantData);
        
        const dataTime = performance.now() - dataStart;
        ////console.log(`📊 Data update time: ${dataTime.toFixed(2)}ms`);
        
        const formStart = performance.now();
        
        // Update form and trigger theme events
        this.updateForm(variantData.variant_id);
        this.triggerThemeEvents(variantData);
        
        const formTime = performance.now() - formStart;
        ////console.log(`📝 Form update time: ${formTime.toFixed(2)}ms`);
        
        const colorNamesStart = performance.now();
        
        // Update color names in UI
        this.updateColorNamesInUI();
        
        const colorNamesTime = performance.now() - colorNamesStart;
        ////console.log(`🎨 Color names update time: ${colorNamesTime.toFixed(2)}ms`);
        
        const totalTime = performance.now() - startTime;
        ////console.log(`✅ Color switch completed in: ${totalTime.toFixed(2)}ms`);
        ////console.log(`📊 Breakdown: Setup(${setupTime.toFixed(2)}ms) + URL(${urlTime.toFixed(2)}ms) + UI(${uiTime.toFixed(2)}ms) + Data(${dataTime.toFixed(2)}ms) + Form(${formTime.toFixed(2)}ms) + ColorNames(${colorNamesTime.toFixed(2)}ms)`);
        console.log('🌐 AFTER COLOR SWITCH - Current URL:', window.location.href);
        
      } catch (error) {
        const errorTime = performance.now() - startTime;
        console.error(`❌ Color switch failed after ${errorTime.toFixed(2)}ms:`, error);
        
        // Revert UI on error
        this.updateColorUI(this.currentColor);
      } finally {
        this.isLoading = false;
      }
    }
    

    
    async switchSize(size) {
      if (this.isLoading || !this.currentColor) return;
      
      this.isLoading = true;
      
      try {
        const variantData = this.variantMapping[this.currentColor][size];
        if (!variantData) {
          return;
        }
        
        // Update current state - user has now explicitly selected a size
        this.currentSize = size;
        this.userHasSelectedSize = true;
        
        // Update global state for test access
        window.currentColor = this.currentColor;
        window.currentSize = size;
        
        // Update URL for size changes
        this.updateURL(variantData.variant_id);
        
        // UPDATE ALL DATA FROM JSON MAPPING - NO AJAX NEEDED
        this.updateAllDataFromMapping(variantData);
        
        // Ensure images are properly displayed for current color
        this.updateProductImages(variantData);
        
        // Update form and trigger theme events
        this.updateForm(variantData.variant_id);
        this.triggerThemeEvents(variantData);
        
      } catch (error) {
        console.error('❌ Size switch failed:', error);
      } finally {
        this.isLoading = false;
      }
    }
    
    async switchSizeByVariantId(variantId) {
      if (this.isLoading || !this.currentColor) return;
      
      console.log('🌐 BEFORE SIZE SWITCH - Current URL:', window.location.href);
      console.log('🎯 Before lookup - Current color:', this.currentColor, 'Current size:', this.currentSize);
      
      this.isLoading = true;
      
      try {
        // First, try to find the size for this variant ID in the current color
        const sizes = this.variantMapping[this.currentColor];
        let targetSize = null;
        let variantData = null;
        
        for (const [size, data] of Object.entries(sizes)) {
          if (data.variant_id.toString() === variantId) {
            targetSize = size;
            variantData = data;
            break;
          }
        }
        
        // If not found, try to map the variant ID to a size based on position
        if (!variantData) {
          // Get all radio buttons to understand the mapping
          const radioButtons = document.querySelectorAll('input[name*="option2"][type="radio"]');
          const radioValues = Array.from(radioButtons).map(radio => radio.value);
          
          // Get the actual size order from the available sizes in this color
          const availableSizes = Object.keys(sizes);
          const variantIndex = radioValues.indexOf(variantId);
          
          if (variantIndex >= 0 && variantIndex < availableSizes.length) {
            const mappedSize = availableSizes[variantIndex];
            if (sizes[mappedSize]) {
              targetSize = mappedSize;
              variantData = sizes[mappedSize];
              console.log(`🔍 Mapped variant ID ${variantId} at position ${variantIndex} to size ${mappedSize}`);
            }
          }
        }
        
        if (!variantData) {
          return;
        }
        
        // Update current state - user has now explicitly selected a size
        this.currentSize = targetSize;
        this.userHasSelectedSize = true;
        
        // Update global state for test access
        window.currentColor = this.currentColor;
        window.currentSize = targetSize;
        
        // Update URL for size changes
        this.updateURL(variantData.variant_id);
        
        // UPDATE SIZE UI IMMEDIATELY
        this.selectSizeInUI(targetSize, variantData.variant_id);
        
        // UPDATE ALL DATA FROM JSON MAPPING - NO AJAX NEEDED
        this.updateAllDataFromMapping(variantData);
        
        // Ensure images are properly displayed for current color
        this.updateProductImages(variantData);
        
        // Update form and trigger theme events
        this.updateForm(variantData.variant_id);
        this.triggerThemeEvents(variantData);
        
        console.log('🎯 After selection - Selected size:', targetSize, 'Selected color:', this.currentColor, 'Variant ID:', variantData.variant_id);
        console.log('🌐 AFTER SIZE SWITCH - Current URL:', window.location.href);
        
      } catch (error) {
        console.error('❌ Size switch by variant ID failed:', error);
      } finally {
        this.isLoading = false;
      }
    }
    
    updateColorUI(color) {
      // Update color swatch selection
      const swatches = document.querySelectorAll('.ajax-color-swatch');
      
      swatches.forEach((swatch, index) => {
        const swatchColor = swatch.getAttribute('data-color');
        const isSelected = swatchColor === color;
        
        swatch.classList.toggle('is-selected', isSelected);
        swatch.classList.toggle('selected', isSelected);
        swatch.setAttribute('aria-pressed', isSelected);
      });
    }
    
    updateURL(variantId) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }
    
    findVariantForColorAndSize(color, size) {
      // Find the variant data for a specific color + size combination
      if (!this.variantMapping || !this.variantMapping[color]) {
        console.warn(`⚠️ No variant mapping found for color: ${color}`);
        return null;
      }
      
      const colorVariants = this.variantMapping[color];
      const variantData = colorVariants[size];
      
      if (variantData) {
        ////console.log(`✅ Found variant for ${color} + ${size}: ${variantData.variant_id}`);
        return variantData;
      } else {
        console.warn(`⚠️ No variant found for ${color} + ${size}`);
        return null;
      }
    }
    
    updateAllDataFromMapping(variantData) {
      const dataUpdateStart = performance.now();
      //console.log('🔄 Starting comprehensive data update...');
      
      // Execute immediately for maximum performance (removed RAF delay)
      const updateStart = performance.now();
        
        // 1. Update Product Title
        const titleStart = performance.now();
        this.updateProductTitle(variantData);
        const titleTime = performance.now() - titleStart;
        ////console.log(`   📝 Title update: ${titleTime.toFixed(2)}ms`);
        
        // 2. Update Product Price
        const priceStart = performance.now();
        this.updateProductPrice(variantData);
        const priceTime = performance.now() - priceStart;
        ////console.log(`   💰 Price update: ${priceTime.toFixed(2)}ms`);
        
        // 3. Update Product Vendor
        const vendorStart = performance.now();
        this.updateProductVendor(variantData);
        const vendorTime = performance.now() - vendorStart;
        ////console.log(`   🏷️ Vendor update: ${vendorTime.toFixed(2)}ms`);
        
        // 4. Update Product SKU
        const skuStart = performance.now();
        this.updateProductSKU(variantData);
        const skuTime = performance.now() - skuStart;
        ////console.log(`   🏷️ SKU update: ${skuTime.toFixed(2)}ms`);
        
        // 5. Update Product Description
        const descStart = performance.now();
        this.updateProductDescription(variantData);
        const descTime = performance.now() - descStart;
        ////console.log(`   📄 Description update: ${descTime.toFixed(2)}ms`);
        
        // 6. Update Product Images
        const imagesStart = performance.now();
        this.updateProductImages(variantData);
        const imagesTime = performance.now() - imagesStart;
        console.log(`   🖼️ Images update: ${imagesTime.toFixed(2)}ms`);
        
        // 7. Update Inventory Information
        const inventoryStart = performance.now();
        this.updateInventoryInfo(variantData);
        const inventoryTime = performance.now() - inventoryStart;
        ////console.log(`   📦 Inventory update: ${inventoryTime.toFixed(2)}ms`);
        
        // 8. Update Button State
        const buttonStart = performance.now();
        this.updateButtonState(variantData);
        const buttonTime = performance.now() - buttonStart;
        ////console.log(`   🔘 Button update: ${buttonTime.toFixed(2)}ms`);
        
        // 9. Update Product Badges
        const badgesStart = performance.now();
		
        this.updateProductBadges(variantData);
        const badgesTime = performance.now() - badgesStart;
        ////console.log(`   🏷️ Badges update: ${badgesTime.toFixed(2)}ms`);
        
        // 10. Update Variant Data Script
        const scriptStart = performance.now();
        this.updateVariantDataScript(variantData);
        const scriptTime = performance.now() - scriptStart;
        ////console.log(`   📜 Script update: ${scriptTime.toFixed(2)}ms`);
        
        // 11. Update BIS Modal Title
        const bisStart = performance.now();
        this.updateBISModalTitle(variantData);
        const bisTime = performance.now() - bisStart;
        ////console.log(`   📋 BIS modal update: ${bisTime.toFixed(2)}ms`);
        
        // 12. Update Product Sticky Bar
        const stickyStart = performance.now();
        this.updateProductStickyBar(variantData);
        const stickyTime = performance.now() - stickyStart;
        ////console.log(`   📌 Sticky bar update: ${stickyTime.toFixed(2)}ms`);
        
        // 13. Trigger theme events after all updates
        const eventsStart = performance.now();
        this.triggerThemeEvents(variantData);
        const eventsTime = performance.now() - eventsStart;
        ////console.log(`   🎯 Theme events: ${eventsTime.toFixed(2)}ms`);
        
                 const updateTime = performance.now() - updateStart;
         const totalDataTime = performance.now() - dataUpdateStart;
         ////console.log(`📊 Data update breakdown: Direct(${updateTime.toFixed(2)}ms) - Title(${titleTime.toFixed(2)}ms) + Price(${priceTime.toFixed(2)}ms) + Vendor(${vendorTime.toFixed(2)}ms) + SKU(${skuTime.toFixed(2)}ms) + Desc(${descTime.toFixed(2)}ms) + Images(${imagesTime.toFixed(2)}ms) + Inventory(${inventoryTime.toFixed(2)}ms) + Button(${buttonTime.toFixed(2)}ms) + Badges(${badgesTime.toFixed(2)}ms) + Script(${scriptTime.toFixed(2)}ms) + BIS(${bisTime.toFixed(2)}ms) + Sticky(${stickyTime.toFixed(2)}ms) + Events(${eventsTime.toFixed(2)}ms) = Total(${totalDataTime.toFixed(2)}ms)`);
    }
    
    updateProductTitle(variantData) {
      // TITLE STRUCTURE ANALYSIS:
      // Expected structure:
      // <h1 class="product-title">Cotton Zipper Hoodie</h1>
      // 
      // UPDATE POINT: Direct text content update is safe for title elements
      // No nested structure to preserve
      
      const titleElements = document.querySelectorAll('.product-info h1.product-title, .product-info .product-title h1, .product-info h1[data-product-title], .product-info .product__title');
      const newTitle = variantData.product_title || variantData.title || 'Product';
      
      titleElements.forEach(element => {
        if (element.tagName === 'A') {
          element.textContent = newTitle;
        } else {
          element.textContent = newTitle;
        }
      });
    }
    
    renderPriceList(targetElement, variantData) {
      //console.log('💰 Rendering price list...');

      // 1. Găsesc v-stack în targetElement
      const vStack = targetElement.querySelector('.v-stack');
      if (!vStack) {
        return;
      }

      // 2. Șterg orice price-list existent din v-stack
      const existingPriceList = vStack.querySelector('price-list');
      if (existingPriceList) {
        existingPriceList.remove();
      }

      if (variantData.price) {
        const formattedPrice = this.formatMoney(variantData.price);
        const hasComparePrice = variantData.compare_at_price && variantData.compare_at_price > 0;

        console.log(`🔍 DEBUG: variantData.price=${variantData.price}, variantData.compare_at_price=${variantData.compare_at_price}, hasComparePrice=${hasComparePrice}`);

        // Construiesc HTML-ul cu web components-urile theme-ului
        // Check if this is sticky bar (different class structure)
        const isStickyBar = targetElement.closest('.product-sticky-bar__info') !== null;
        const priceListClass = isStickyBar ? 'price-list' : 'price-list price-list--product';
        let priceHTML = `<price-list class="${priceListClass}">`;
		let index = 0;

		// Mereu pun sale-price
		if (hasComparePrice){
			priceHTML += '<sale-price class="h4 text-on-sale"><span class="sr-only">Sale price</span>'+formattedPrice+'</sale-price>';
			console.log(`🔍 DEBUG: Added sale-price with text-on-sale`);
		}else{
			priceHTML += '<sale-price class="h4 text-subdued"><span class="sr-only">Sale price</span>'+formattedPrice+'</sale-price>';
			console.log(`🔍 DEBUG: Added sale-price with text-subdued`);
		}
		index++;

        // Add Black Friday price if available (between sale-price and compare-at-price)
        if (variantData.black_friday_price && variantData.black_friday_price > 0) {
          const priceBlock = document.querySelector('.product-info__block-item[data-block-id="price"]');
          if (priceBlock) {
            const blockSettings = priceBlock.querySelector('[data-black-friday-settings]');
            if (blockSettings) {
              const enableBFPrice = blockSettings.getAttribute('data-enable-black-friday-price') === 'true';
              if (enableBFPrice) {
                const formattedBFPrice = this.formatMoney(variantData.black_friday_price);
                const bfColor = blockSettings.getAttribute('data-bf-color') || '#cc0000';
                const bfSpacePx = blockSettings.getAttribute('data-bf-space') || '8';
                const bfFontSizeDesktop = blockSettings.getAttribute('data-bf-font-desktop') || '24';
                const bfFontSizeMobile = blockSettings.getAttribute('data-bf-font-mobile') || '18';
                const bfStrikethrough = blockSettings.getAttribute('data-bf-strikethrough') === 'true';
                const strikethroughStyle = bfStrikethrough ? ' text-decoration: line-through;' : '';

                priceHTML += `<black-friday-price class="black-friday-price" style="margin-left: ${bfSpacePx}px; margin-right: ${bfSpacePx}px; display: flex; gap: 0.5rem; align-items: center;">`;
                priceHTML += `<span class="black-friday-price__label" style="color: ${bfColor}; font-family: var(--heading-font-family); font-weight: var(--heading-font-weight); font-size: clamp(${bfFontSizeMobile}px, 5vw, ${bfFontSizeDesktop}px); line-height: 1;${strikethroughStyle}">${formattedBFPrice}</span>`;
                priceHTML += `</black-friday-price>`;
              }
            }
          }
        }

        // Adaug compare-at-price doar dacă există
        if (hasComparePrice) {
          const formattedComparePrice = this.formatMoney(variantData.compare_at_price);
          priceHTML += `<compare-at-price class="h5 text-subdued line-through"><span class="sr-only">Regular price</span>${formattedComparePrice}</compare-at-price>`;
		  index++;
        }

        priceHTML += `</price-list>`;

        // Insert new price-list after settings div (or at the start if no settings div exists)
        // This preserves the data-black-friday-settings div
        const settingsDiv = vStack.querySelector('[data-black-friday-settings]');
        if (settingsDiv) {
          settingsDiv.insertAdjacentHTML('afterend', priceHTML);
        } else {
          vStack.insertAdjacentHTML('afterbegin', priceHTML);
        }
		//debugger;
        console.log(`   ✅ Rendered price list: ${formattedPrice} with index ${index}`);
      }
    }
    
    updateProductPrice(variantData) {
      //console.log('💰 Updating product price...');
      
      const priceContainer = document.querySelector('.product.product-variations .product-info .product-info__block-item[data-block-id="price"]');
      
      if (priceContainer && variantData.price) {
		console.log('💰 Updating product price...');
        this.renderPriceList(priceContainer, variantData);
      }
    }
    
    updateProductVendor(variantData) {
      // VENDOR STRUCTURE ANALYSIS:
      // Expected structure (needs verification from debug test):
      // <div class="vendor">Brand Name</div>
      // 
      // UPDATE POINT: Direct text content update is safe
      // No nested structure to preserve
      
      const vendorElements = document.querySelectorAll('.product-info .vendor, .product-info .product-vendor, .product-info [data-vendor]');
      const newVendor = variantData.vendor || '';
      
      vendorElements.forEach(element => {
        element.textContent = newVendor;
      });
    }
    
    updateProductSKU(variantData) {
      const skuElements = document.querySelectorAll('.variant-sku, [data-sku]');
      const newSKU = variantData.sku || '';
      
      skuElements.forEach(element => {
        if (newSKU) {
          element.textContent = `SKU ${newSKU}`;
          element.style.display = 'block';
        } else {
          element.style.display = 'none';
        }
      });
    }
    
    updateProductDescription(variantData) {
      const descriptionElements = document.querySelectorAll('.product-description, .rte, [data-description]');
      const newDescription = variantData.description || '';
      
      descriptionElements.forEach(element => {
        element.innerHTML = newDescription;
      });
    }
    
    updateProductImages(variantData) {
      console.log('🖼️ Updating product images using hide/show approach...');
      
      // Hide all images first
      this.hideAllImages();
      
      // Show images for the current color
      this.showImagesForColor(this.currentColor);

      // Prefer theme-native filtering so navigation (page-dots) stays in sync
      if (typeof this.applyCarouselFilterForColor === 'function') {
        console.log('🐛 [DEBUG] Applying theme-native carousel filter for color:', this.currentColor);
        this.applyCarouselFilterForColor(this.currentColor);
      } else {
        console.warn('⚠️ [DEBUG] applyCarouselFilterForColor not available, skipping theme-native filter');
      }
      
      // Reset swiper/carousel to first slide (no heavy DOM reinit)
      this.resetCarouselToFirstSlide();

      // Ensure nav dots click map to the correct global indices for this color
      if (typeof this.bindNavDotsToColor === 'function') {
        this.bindNavDotsToColor(this.currentColor);
      }
      
      console.log(`✅ Images updated for color: ${this.currentColor}`);
    }
    

    
    hideAllImages() {
      // Hide all gallery image containers
      const allImageContainers = document.querySelectorAll('.product-gallery__carousel  .product-gallery__media[data-color]');
      allImageContainers.forEach(container => {		
        if (!container.classList.contains('hidden')) {
          container.classList.add('hidden');
        }
      });
      console.log(`🔒 Hidden ${allImageContainers.length} image containers`);
    }
    
    showImagesForColor(color) {
      // Show gallery image containers for the specified color
      const colorImageContainers = document.querySelectorAll(`.product-gallery__carousel .product-gallery__media[data-color="${color}"]`);
      colorImageContainers.forEach(container => {
		if (container.classList.contains('hidden')) {
			container.classList.remove('hidden');
		}
		//remove display none as well
		container.style.display = '';
      });
      
      console.log(`   ✅ Showing ${colorImageContainers.length} image containers for color: ${color}`);
    }
    
    resetCarouselToFirstSlide() {
      // Find the product gallery carousel and reset it to the first slide
      const carousel = document.querySelector('.product-gallery__carousel');
      
      if (carousel && typeof carousel.select === 'function') {
        // Use instant: true to skip animation and jump directly to first slide
        carousel.select(0, { instant: true });
        console.log('🔄 Reset carousel to first slide (instant)');
      } else {
        // Fallback: try to find any carousel element with select method
        const carousels = document.querySelectorAll('[class*="carousel"]');
        for (const carouselElement of carousels) {
          if (typeof carouselElement.select === 'function') {
            carouselElement.select(0, { instant: true });
            console.log('🔄 Reset carousel to first slide (instant fallback)');
            break;
          }
        }
      }
    }
    
    
    applyCarouselFilterForColor(color) {
      try {
        const gallery = document.querySelector('product-gallery') || document.querySelector('.product-gallery');
        const carousel =
          (gallery && (gallery.querySelector('scroll-carousel.product-gallery__carousel') || gallery.querySelector('.product-gallery__carousel')))
          || document.querySelector('scroll-carousel.product-gallery__carousel')
          || document.querySelector('.product-gallery__carousel');

        if (!carousel) {
          console.warn('⚠️ [DEBUG] No carousel found for filter application');
          return;
        }

        // Ensure the navigation points to the CURRENT carousel id and is unhidden
        try {
          const nav = (gallery && gallery.querySelector('carousel-navigation.page-dots'))
            || document.querySelector('carousel-navigation.page-dots');
          if (nav) {
            // Make sure aria-controls points to the current carousel
            if (nav.getAttribute('aria-controls') !== carousel.id) {
              console.log('🐛 [DEBUG] Updating nav aria-controls ->', carousel.id);
              nav.setAttribute('aria-controls', carousel.id);
            }
            // Unhide the nav if it was hidden by the theme
            if (nav.hasAttribute('hidden')) {
              console.log('🐛 [DEBUG] Removing [hidden] from carousel-navigation (pre-filter sync)');
              nav.removeAttribute('hidden');
            }
            // Force reconnect: replace nav with a clone so connectedCallback rebinds to the current carousel
            try {
              const navClone = nav.cloneNode(true);
              navClone.setAttribute('aria-controls', carousel.id);
              nav.replaceWith(navClone);
              console.log('🐛 [DEBUG] Replaced carousel-navigation to force reconnect');
            } catch (reconnectErr) {
              console.warn('⚠️ [DEBUG] Could not force reconnect on nav:', reconnectErr);
            }
            // Unhide all dot buttons (listeners are re-attached in connectedCallback on the clone)
            const navBtns = Array.from((document.querySelector(`carousel-navigation.page-dots[aria-controls="${carousel.id}"]`) || nav).querySelectorAll('button'));
            navBtns.forEach((btn) => btn.removeAttribute('hidden'));
          } else {
            console.log('🐛 [DEBUG] No nav found near gallery to sync aria-controls with carousel');
          }
        } catch (syncErr) {
          console.warn('⚠️ [DEBUG] Error syncing nav aria-controls:', syncErr);
        }

        // Use allCells when available so indexes map correctly to filter()
        const sourceCells = Array.isArray(carousel.allCells)
          ? carousel.allCells
          : (carousel.allCells ? Array.from(carousel.allCells) : Array.from(carousel.querySelectorAll('.product-gallery__media')));

        const keepIndexes = [];
        let hideIndexes = [];

        sourceCells.forEach((cell, idx) => {
          const el = cell?.element || cell;
          const cellColor = el?.getAttribute ? el.getAttribute('data-color') : null;
          if (cellColor === color) {
            keepIndexes.push(idx);
          } else {
            hideIndexes.push(idx);
          }
        });

        // Recompute hideIndexes as the complement of keepIndexes to avoid gaps
        const allIdxs = Array.from({ length: sourceCells.length }, (_, i) => i);
        const keepSet = new Set(keepIndexes);
        hideIndexes = allIdxs.filter(i => !keepSet.has(i));

        console.log('🐛 [DEBUG] allCells length:', sourceCells.length, 'keepIndexes:', keepIndexes, 'hideIndexes (complement):', hideIndexes);

        // If the navigation already renders only the kept slides (common for Prestige after variant change),
        // avoid using carousel.filter which would hide all nav buttons due to index mismatch.

        if (typeof carousel.filter === 'function') {
          // Clear any previous filtering so navigation does not hide buttons incorrectly
          carousel.filter([]);

          // Keep product-gallery attribute in sync (empty list means show all)
          const pg = gallery || document.querySelector('product-gallery');
          if (pg) {
            try {
              pg.setAttribute('filtered-indexes', JSON.stringify([]));
              console.log('🐛 [DEBUG] Reset product-gallery filtered-indexes to empty (show all)');
            } catch {}
          }

          // Make sure the dots nav is not hidden when there are multiple kept items
          try {
            const nav = document.querySelector(`carousel-navigation.page-dots[aria-controls="${carousel.id}"]`);
            if (nav) {
              if (keepIndexes.length > 1) {
                // Ensure the nav itself is unhidden
                if (nav.hasAttribute('hidden')) {
                  console.log('🐛 [DEBUG] Removing [hidden] from carousel-navigation');
                  nav.removeAttribute('hidden');
                  // Re-check after paint and a short delay, plus after carousel:filter event
                  requestAnimationFrame(() => {
                    const navLater = document.querySelector(`carousel-navigation.page-dots[aria-controls="${carousel.id}"]`);
                    if (navLater && navLater.hasAttribute('hidden')) {
                      console.log('🐛 [DEBUG] Removing [hidden] from carousel-navigation (rAF)');
                      navLater.removeAttribute('hidden');
                    }
                  });
                  setTimeout(() => {
                    const navLater2 = document.querySelector(`carousel-navigation.page-dots[aria-controls="${carousel.id}"]`);
                    if (navLater2 && navLater2.hasAttribute('hidden')) {
                      console.log('🐛 [DEBUG] Removing [hidden] from carousel-navigation (timeout)');
                      navLater2.removeAttribute('hidden');
                    }
                  }, 300);
                  if (typeof carousel.addEventListener === 'function') {
                    const onFilterOnce = () => {
                      const navEvent = document.querySelector(`carousel-navigation.page-dots[aria-controls="${carousel.id}"]`);
                      if (navEvent && navEvent.hasAttribute('hidden')) {
                        console.log('🐛 [DEBUG] Removing [hidden] from carousel-navigation (on carousel:filter)');
                        navEvent.removeAttribute('hidden');
                      }
                    };
                    carousel.addEventListener('carousel:filter', onFilterOnce, { once: true });
                  }
                }
                // Ensure each button visibility matches keepIndexes (defensive, theme should also handle via carousel:filter)
                const navButtons = Array.from(nav.querySelectorAll('button'));
                // Unhide all nav buttons; theme's own filtering may already reflect kept slides,
                // so per-index hiding based on global indexes can incorrectly hide everything.
                navButtons.forEach((btn) => {
                  if (btn.hasAttribute('hidden')) btn.removeAttribute('hidden');
                  // Clear any inline display override we might have previously applied
                  btn.style.display = '';
                });
              } else {
                console.log('🐛 [DEBUG] Single (or zero) visible slides; nav may be hidden by theme');
              }
            } else {
              console.log('🐛 [DEBUG] No carousel-navigation found for aria-controls:', carousel.id);
            }
          } catch (navErr) {
            console.warn('⚠️ [DEBUG] Error ensuring nav visibility:', navErr);
          }

          // Ensure we start from the first visible slide
          if (typeof carousel.select === 'function') {
            console.log('🐛 [DEBUG] Selecting first visible slide after filter');
            carousel.select(0, { instant: true });
            try {
              const detail = { index: carousel.selectedIndex, cell: carousel.cells ? carousel.cells[carousel.selectedIndex] : undefined };
              carousel.dispatchEvent(new CustomEvent('carousel:change', { detail }));
            } catch (emitErr) {
              console.warn('⚠️ [DEBUG] Unable to emit carousel:change:', emitErr);
            }
          }
        } else {
          console.warn('⚠️ [DEBUG] carousel.filter is not a function, skipping');
        }
      } catch (e) {
        console.warn('⚠️ [DEBUG] Error applying carousel filter:', e);
      }
      try {
        this.bindNavDotsToColor(color);
      } catch (e) {
        console.warn('⚠️ [DEBUG] bindNavDotsToColor error:', e);
      }
    }

    bindNavDotsToColor(color) {
      try {
        const gallery = document.querySelector('product-gallery') || document.querySelector('.product-gallery');
        const carousel =
          (gallery && (gallery.querySelector('scroll-carousel.product-gallery__carousel') || gallery.querySelector('.product-gallery__carousel')))
          || document.querySelector('scroll-carousel.product-gallery__carousel')
          || document.querySelector('.product-gallery__carousel');

        if (!carousel) {
          console.warn('⚠️ [DEBUG] bindNavDotsToColor: no carousel');
          return;
        }

        // Compute visible cell indexes for this color
        const sourceCells = Array.isArray(carousel.allCells)
          ? carousel.allCells
          : (carousel.allCells ? Array.from(carousel.allCells) : Array.from(carousel.querySelectorAll('.product-gallery__media')));

        const visibleIdx = [];
        sourceCells.forEach((cell, idx) => {
          const el = cell?.element || cell;
          const hasGet = !!(el && el.getAttribute);
          const cellColor = hasGet ? el.getAttribute('data-color') : null;
          const isAttrHidden = hasGet ? el.hasAttribute('hidden') : false;
          const isCssHidden = el?.classList?.contains('hidden');
          if (cellColor === color && !isAttrHidden && !isCssHidden) {
            visibleIdx.push(idx);
          }
        });

        const nav = (gallery && gallery.querySelector(`carousel-navigation.page-dots[aria-controls="${carousel.id}"]`))
          || document.querySelector(`carousel-navigation.page-dots[aria-controls="${carousel.id}"]`);

        if (!nav) {
          console.warn('⚠️ [DEBUG] bindNavDotsToColor: no nav for carousel id', carousel.id);
          return;
        }

        // Recreate nav to clear any previous handlers and force reconnect to current carousel
        const navClone = nav.cloneNode(true);
        navClone.setAttribute('aria-controls', carousel.id);
        nav.replaceWith(navClone);

        // Unhide nav and all buttons
        navClone.removeAttribute('hidden');
        const buttons = Array.from(navClone.querySelectorAll('button'));
        buttons.forEach(b => b.removeAttribute('hidden'));

        // Map each visible index to a dot (limited by whichever is smaller)
        const count = Math.min(buttons.length, visibleIdx.length);

        const handleClick = (targetIndex) => (evt) => {
          try {
            evt?.preventDefault?.();
            if (typeof carousel.select === 'function') {
              carousel.select(targetIndex);
            } else {
              const cellEl = sourceCells[targetIndex]?.element || sourceCells[targetIndex];
              cellEl?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
            }
          } catch (e) {
            console.warn('⚠️ [DEBUG] bindNavDotsToColor click error:', e);
          }
        };

        for (let i = 0; i < count; i++) {
          const btn = buttons[i];
          const targetIdx = visibleIdx[i];
          btn.setAttribute('type', 'button');
          btn.setAttribute('data-target-index', String(targetIdx));
          btn.onclick = handleClick(targetIdx);
          btn.removeAttribute('hidden');
        }
        // Hide or disable any extra buttons that don't map to a visible slide
        for (let i = count; i < buttons.length; i++) {
          const btn = buttons[i];
          btn.removeAttribute('onclick');
          btn.onclick = null;
          btn.setAttribute('hidden', '');
          btn.setAttribute('aria-current', 'false');
        }

        // Keep aria-current in sync with carousel changes
        const updateAriaCurrent = () => {
          const current = typeof carousel.selectedIndex === 'number' ? carousel.selectedIndex : 0;
          for (let i = 0; i < count; i++) {
            const btn = buttons[i];
            const targetIdx = visibleIdx[i];
            btn.setAttribute('aria-current', targetIdx === current ? 'true' : 'false');
          }
        };

        try {
          carousel.addEventListener('carousel:change', updateAriaCurrent);
          carousel.addEventListener('carousel:settle', updateAriaCurrent);
        } catch (e) {}
        updateAriaCurrent();

        console.log('🐛 [DEBUG] bindNavDotsToColor wired', { color, count, visibleIdx });
      } catch (e) {
        console.warn('⚠️ [DEBUG] bindNavDotsToColor error:', e);
      }
    }

    reinitializeCarousel() {
      // Force re-initialization of the entire product gallery section
      const productGallery = document.querySelector('.product-gallery');
      if (!productGallery) return;
      
      try {
        // Store the entire product gallery HTML content
        const galleryHTML = productGallery.outerHTML;
        const parent = productGallery.parentNode;
        
        // Remove the entire gallery from DOM
        productGallery.remove();
        
        // Re-insert the gallery HTML
        parent.insertAdjacentHTML('beforeend', galleryHTML);
        
        // Find the new carousel element
        const newCarousel = parent.querySelector('.product-gallery__carousel');
        
        // Reset to first slide on the new carousel
        if (newCarousel && typeof newCarousel.select === 'function') {
          newCarousel.select(0, { instant: true });
        }
        
        // Re-init Swiper pagination on mobile
        if (window.innerWidth < 1000 && window.Swiper) {
          if (this.swiperInstance && this.swiperInstance.destroy) {
            this.swiperInstance.destroy(true, true);
          }
          if (newCarousel) {
            this.swiperInstance = new Swiper(newCarousel, {
              pagination: {
                el: '.swiper-pagination',
                clickable: true
              },
              observer: true,
              observeParents: true
            });
          }
        }
        
        // Ensure custom carousel + pagination reconnect on mobile
        try {
          if (newCarousel && typeof newCarousel.connect === 'function') {
            newCarousel.connect();
      
            // Extra deep debug: list children and check for carousel-pagination
            if (debugCarousel) {
              console.log('🐛 [DEBUG] Carousel innerHTML snippet:', debugCarousel.innerHTML.slice(0, 500));
              const foundPagination = debugCarousel.querySelector('carousel-pagination');
              console.log('🐛 [DEBUG] carousel-pagination inside carousel:', foundPagination);
            }
      
            // Try to reconnect <carousel-navigation> (dots bullets)
            const navDots = parent.querySelector('carousel-navigation.page-dots');
            console.log('🐛 [DEBUG] carousel-navigation element:', navDots);
            if (navDots) {
              console.log('🐛 [DEBUG] navDots innerHTML:', navDots.innerHTML);
              console.log('🐛 [DEBUG] navDots attributes:', navDots.getAttributeNames().reduce((acc, name) => { acc[name] = navDots.getAttribute(name); return acc; }, {}));
              console.log('🐛 [DEBUG] navDots prototype methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(navDots)));

              if (typeof navDots.connect === 'function') {
                console.log('🐛 [DEBUG] Calling navDots.connect()');
                try {
                  navDots.connect();
                  console.log('✅ [DEBUG] navDots.connect() executed successfully');
                } catch (err) {
                  console.warn('⚠️ [DEBUG] navDots.connect() error:', err);
                }
              } else if (typeof navDots.connectedCallback === 'function') {
                console.log('🐛 [DEBUG] Calling navDots.connectedCallback()');
                try {
                  navDots.connectedCallback();
                  console.log('✅ [DEBUG] navDots.connectedCallback() executed successfully');
                } catch (err) {
                  console.warn('⚠️ [DEBUG] navDots.connectedCallback() error:', err);
                }
              } else {
                console.log('🐛 [DEBUG] Dispatching carousel:init event on navDots');
                navDots.dispatchEvent(new Event('carousel:init', { bubbles: true }));
              }

              // Ensure aria-controls points to the latest carousel id
              const newCarousel = parent.querySelector('scroll-carousel');
              if (newCarousel) {
                console.log('🐛 [DEBUG] New carousel id =', newCarousel.id);
                console.log('🐛 [DEBUG] navDots aria-controls BEFORE =', navDots.getAttribute('aria-controls'));
                navDots.setAttribute('aria-controls', newCarousel.id);
                console.log('🐛 [DEBUG] navDots aria-controls AFTER =', navDots.getAttribute('aria-controls'));
              } else {
                console.warn('⚠️ [DEBUG] No scroll-carousel found when updating navDots linkage');
              }

              // Re-append navDots to trigger connectedCallback
              const replacedNavDots = navDots.cloneNode(true);
              navDots.replaceWith(replacedNavDots);
              console.log('🐛 [DEBUG] Replaced navDots element to retrigger connectedCallback');

              // Attach click logging for each new bullet button
              const navButtons = replacedNavDots.querySelectorAll('button');
              console.log(`🐛 [DEBUG] Found ${navButtons.length} navDots buttons after replacement`);
              navButtons.forEach((btn, idx) => {
                console.log(`🐛 [DEBUG] Attaching click listener to navDot[${idx}]`, btn);
                btn.addEventListener('click', (evt) => {
                  console.log(`🐛 [DEBUG] CLICKED navDot[${idx}]`, {
                    element: btn,
                    ariaCurrent: btn.getAttribute('aria-current'),
                    label: btn.querySelector('.sr-only')?.innerText,
                    event: evt
                  });
                });
              });
            } else {
              console.warn('⚠️ [DEBUG] navDots element not found in parent:', parent);
            }
          }
          const pagination = parent.querySelector('carousel-pagination');
          if (pagination && typeof pagination.connect === 'function') {
            pagination.connect();
          }
        } catch(e) {
          console.warn('⚠️ Could not reconnect carousel pagination:', e);
        }
        
        console.log('🔄 Re-initialized entire product gallery to fix pagination');
      } catch (error) {
        console.log('⚠️ Could not re-initialize product gallery:', error);
      }

      // Debug logging for carousel reconnect
      const debugCarousel = document.querySelector('.product-gallery__carousel');
      const debugPagination = document.querySelector('carousel-pagination');
      console.log('🐛 [DEBUG] reinitializeCarousel called');
      console.log('🐛 [DEBUG] Found carousel element:', debugCarousel);
      if (debugCarousel) {
        console.log('🐛 [DEBUG] Carousel methods:', Object.keys(debugCarousel));
      }
      console.log('🐛 [DEBUG] Found pagination element:', debugPagination);
      if (debugPagination) {
        console.log('🐛 [DEBUG] Pagination methods:', Object.keys(debugPagination));
      }
    }
    
    updateInventoryInfo(variantData) {
      // INVENTORY STRUCTURE ANALYSIS:
      // Expected structure (needs verification from debug test):
      // <div class="inventory">5 in stock</div>
      // 
      // UPDATE POINTS:
      // - textContent: element.textContent = newText
      // - className: element.className = newClasses
      // 
      // NOTE: Selectors may need adjustment based on actual DOM structure
      
      const inventoryElements = document.querySelectorAll('.inventory, .product__stock-info, .product__availability, [data-stock-info], .stock-info');
      
      const inventory = variantData.inventory || 0;
      const available = variantData.available || false;
      const maxPreOrders = variantData.max_pre_orders || 0;
      
      let textContent = '';
      let statusClass = '';
      
      if (inventory <= 0 && !available) {
        textContent = 'Out of stock';
        statusClass = 'text-error';
      } else if (inventory > 0) {
        textContent = `${inventory} in stock`;
        statusClass = 'text-success';
      } else if (inventory <= 0 && inventory > maxPreOrders) {
        textContent = 'Re-stocking soon';
        statusClass = 'text-warning';
      } else {
        textContent = 'Out of stock';
        statusClass = 'text-error';
      }
      
      inventoryElements.forEach(element => {
        element.textContent = textContent;
        element.className = '';
        element.classList.add('inventory', 'product__stock-info', 'product__availability', statusClass);
      });
    }
    
    updateButtonState(variantData) {
      //console.log('🔘 Updating button state...');
      
      // BUTTON STRUCTURE ANALYSIS:
      // Found in smart test:
      // Button 1: "Notify Me When Available" class="button button--solid bis-submit primary" type="submit"
      // Button 2: "Select size" class="button w-full disabled select-size-disabled" type="submit"
      // Button 3: "Add to cart" class="button" type="submit"
      // 
      // UPDATE POINTS:
      // - textContent: button.textContent = newText
      // - disabled: button.disabled = true/false
      // - type: button.type = 'submit' or 'button'
      // - className: button.classList.add/remove()
      
      const buttonSelectors = [
        // Main product form buttons - target only the buy_buttons block
        '[data-block-id="buy_buttons"] .shopify-product-form button[type="submit"]',
        '[data-block-id="buy_buttons"] .shopify-product-form .button',
        '[data-block-id="buy_buttons"] .shopify-product-form .product-form__submit',
        '[data-block-id="buy_buttons"] .shopify-product-form [data-add-to-cart]',
        // Sticky bar buttons - target only the sticky bar
        '.product-sticky-bar button[type="submit"]',
        '.product-sticky-bar .button',
        '.product-sticky-bar .product-form__submit',
        '.product-sticky-bar [data-add-to-cart]'
      ];
      
      let buttonUpdated = false;
      
      buttonSelectors.forEach(selector => {
        const buttons = document.querySelectorAll(selector);
        buttons.forEach(button => {
          try {
            const inventory = variantData.inventory || 0;
            const maxPreOrders = variantData.max_pre_orders || 0;
            const available = variantData.available || false;
            const isOutOfStock = inventory <= 0 && !available;
            const isPreOrder = inventory <= 0 && inventory > maxPreOrders;
            
            let buttonText = 'Select size';			
            let buttonDisabled = true;
			this.buttonState = BUTTON_STATES.SELECT_SIZE;
            
            if (this.userHasSelectedSize && this.currentSize) {
              if (isOutOfStock) {
                buttonText = 'NOTIFY ME WHEN AVAILABLE';
				this.buttonState = BUTTON_STATES.NOTIFY_ME;
                buttonDisabled = false;
              } else if (inventory > 0 && available) {
                buttonText = 'Add to cart';
				this.buttonState = BUTTON_STATES.ADD_TO_CART;
                buttonDisabled = false;
              } else if (isPreOrder && available) {
                buttonText = 'Pre-order';
				this.buttonState = BUTTON_STATES.PRE_ORDER;
                buttonDisabled = false;
              } else {
				console.log('this is a funny state. how did we get here?');
                buttonText = 'NOTIFY ME WHEN AVAILABLE';
				this.buttonState = BUTTON_STATES.NOTIFY_ME;
                buttonDisabled = false;
              }
            }
            
            // Update button properties
            button.textContent = buttonText;
            button.disabled = buttonDisabled;
            button.classList.remove('button--outline');
            
            // Handle CSS classes for button styling
            if (buttonDisabled) {
              // Button is disabled - add gray styling classes
              button.classList.add('select-size-disabled');
              button.classList.add('disabled');
            } else {
              // Button is enabled - remove gray styling classes
              button.classList.remove('select-size-disabled');
              button.classList.remove('disabled');
            }
            
            // Handle BIS button conversion
            if (this.buttonState === BUTTON_STATES.NOTIFY_ME) {
              if (!button.classList.contains('my-bis-button')) {
                button.classList.add('my-bis-button');
              }
              button.type = 'button';
              button.setAttribute('data-product-title', this.currentColor || 'Product');
              button.setAttribute('data-variant-id', variantData.variant_id);
              button.setAttribute('data-variant-title', `${this.currentColor} / ${this.currentSize}`);
              
              // Re-wire BIS events if needed
              if (window.wireNotifyButtons) {
                window.wireNotifyButtons();
              }
            } else {
              button.type = 'submit';
              button.classList.remove('my-bis-button');
              button.removeAttribute('data-product-title');
              button.removeAttribute('data-variant-id');
              button.removeAttribute('data-variant-title');
            }
            
            // Handle pre-order text (same logic as Liquid template)
            const buyButtonsContainer = button.closest('.buy-buttons');
            if (buyButtonsContainer) {
              let preOrderText = buyButtonsContainer.querySelector('.text-sm.text-subdued.mt-2');
              if (!preOrderText) {
                preOrderText = document.createElement('p');
                preOrderText.className = 'text-sm text-subdued mt-2';
                buyButtonsContainer.appendChild(preOrderText);
              }
              
              if (this.buttonState === BUTTON_STATES.PRE_ORDER) {
                const dispatchDate = variantData.dispatch_date || 'TBD';
                preOrderText.textContent = `Pre-order: Item ships on ${dispatchDate}`;
              } else {
                preOrderText.textContent = '';
              }
            }
            
            buttonUpdated = true;
            //console.log(`   ✅ Updated button: ${buttonText} (disabled: ${buttonDisabled})`);
          } catch (error) {
            console.warn(`   ⚠️ Error updating button: ${error.message}`);
          }
        });
      });
      
      if (!buttonUpdated) {
        console.warn('   ⚠️ No button elements found to update');
      }
    }
    
    updateProductBadges(variantData) {
      //console.log('🏷️ Updating product badges...');
      
      // Ensure badge structure exists
      let badgeContainer = document.querySelector('.badge-list');
      let badgeBlockItem = document.querySelector('.product-info__block-item[data-block-type="badges"]');
      
      // If badge block item doesn't exist, create it
      if (!badgeBlockItem) {
        //console.log('   🔧 Creating missing badge block item...');
        const productInfoBlockList = document.querySelector('.product-info__block-list');
        if (productInfoBlockList) {
          badgeBlockItem = document.createElement('div');
          badgeBlockItem.className = 'product-info__block-item';
          badgeBlockItem.setAttribute('data-block-id', 'badges_dynamic');
          badgeBlockItem.setAttribute('data-block-type', 'badges');
          
          // Insert after title block or at the beginning
          const titleBlock = productInfoBlockList.querySelector('[data-block-type="title"]');
          if (titleBlock) {
            titleBlock.parentNode.insertBefore(badgeBlockItem, titleBlock.nextSibling);
          } else {
            productInfoBlockList.insertBefore(badgeBlockItem, productInfoBlockList.firstChild);
          }
        }
      }
      
      // If badge container doesn't exist, create it
      if (!badgeContainer && badgeBlockItem) {
        //console.log('   🔧 Creating missing badge container...');
        badgeContainer = document.createElement('badge-list');
        badgeContainer.className = 'badge-list';
        badgeBlockItem.appendChild(badgeContainer);
      }
      
      // Clear existing badges first (except placeholder)
      if (badgeContainer) {
        // Clear all badge-like elements, not just those with .badge class
        const existingBadges = badgeContainer.querySelectorAll('.badge, .badge--on-sale, .badge--custom, .badge--sold-out, sold-out-badge');
        existingBadges.forEach(badge => badge.remove());
        //console.log(`   🧹 Cleared ${existingBadges.length} existing badges`);
      }
      
      // Also check for badges in other common locations - use more specific selectors
      const otherBadgeContainers = document.querySelectorAll('.product-badges, .product__badges, .badge-container, .badge-list');
      otherBadgeContainers.forEach(container => {
        if (container !== badgeContainer) {
          const otherBadges = container.querySelectorAll('.badge, .badge--on-sale, .badge--custom, .badge--sold-out, sold-out-badge');
          otherBadges.forEach(badge => badge.remove());
          if (otherBadges.length > 0) {
            //console.log(`   🧹 Cleared ${otherBadges.length} badges from additional container`);
          }
        }
      });
      
      // Clear badges from product gallery/media areas (where image badges often appear)
      const galleryBadges = document.querySelectorAll('.product-gallery .badge, .product-gallery .badge--on-sale, .product-gallery .badge--custom, .product-gallery .badge--sold-out, .product-media .badge, .product-media .badge--on-sale, .product-media .badge--custom, .product-media .badge--sold-out');
      galleryBadges.forEach(badge => badge.remove());
      if (galleryBadges.length > 0) {
        //console.log(`   🧹 Cleared ${galleryBadges.length} badges from gallery/media areas`);
      }
      
      // Clear any remaining badge-like elements in the product area - ONLY from badge containers
      const badgeContainers = document.querySelectorAll('.badge-list, .product-badges, .product__badges, .badge-container');
      badgeContainers.forEach(container => {
        const remainingBadges = container.querySelectorAll('.badge, .badge--on-sale, .badge--custom, .badge--sold-out, sold-out-badge');
        remainingBadges.forEach(badge => {
          badge.remove();
          //console.log(`   🧹 Cleared badge from container: ${badge.textContent}`);
        });
      });
      
      let badgesUpdated = false;
      
      // 1. Handle sale badge
      const hasSale = variantData.compare_at_price && variantData.compare_at_price > variantData.price;
      if (hasSale) {
        const discount = Math.round(((variantData.compare_at_price - variantData.price) / variantData.compare_at_price) * 100);
        
        const saleBadge = document.createElement('span');
        saleBadge.className = 'badge badge--on-sale';
        saleBadge.textContent = `${discount}% OFF`;
        badgeContainer.appendChild(saleBadge);
        badgesUpdated = true;
        //console.log(`   ✅ Added sale badge: ${discount}% OFF`);
      }
      
      // 2. Handle custom badges from variant data
      if (variantData.custom_badges && Array.isArray(variantData.custom_badges)) {
        variantData.custom_badges.forEach(badgeText => {
          const customBadge = document.createElement('span');
          customBadge.className = `badge badge--custom badge--${badgeText.toLowerCase().replace(/\s+/g, '-')}`;
          customBadge.textContent = badgeText;
          badgeContainer.appendChild(customBadge);
          badgesUpdated = true;
          //console.log(`   ✅ Added custom badge: ${badgeText}`);
        });
      }
      
      // 3. Handle sold out badge
      if (!variantData.available) {
        const soldOutBadge = document.createElement('sold-out-badge');
        soldOutBadge.className = 'badge badge--sold-out';
        soldOutBadge.textContent = 'Sold out';
        badgeContainer.appendChild(soldOutBadge);
        badgesUpdated = true;
        //console.log('   ✅ Added sold out badge');
      }
      
      // Handle placeholder visibility
      if (badgeContainer) {
        let placeholder = badgeContainer.querySelector('.badge-placeholder');
        
        if (badgesUpdated) {
          // Hide placeholder when we have real badges
          if (placeholder) {
            placeholder.style.display = 'none';
            //console.log('   ✅ Hidden badge placeholder (real badges present)');
          }
        } else {
          // Show placeholder when no badges are visible
          if (!placeholder) {
            placeholder = document.createElement('span');
            placeholder.className = 'badge-placeholder';
            placeholder.style.cssText = 'opacity: 0; visibility: hidden; min-height: 20px; line-height: 1.2;';
            placeholder.innerHTML = '&nbsp;';
            badgeContainer.appendChild(placeholder);
          }
          placeholder.style.display = 'block';
          //console.log('   ✅ Added badge placeholder (no badges)');
        }
      }
      
      if (!badgesUpdated) {
        //console.log('   ℹ️ No badges to display');
      }
    }
    
    updateVariantDataScript(variantData) {
      const variantScript = document.querySelector('script[data-variant]');
      if (variantScript) {
        const variant = {
          id: variantData.variant_id,
          title: `${this.currentColor} / ${this.currentSize}`,
          option1: this.currentColor,
          option2: this.currentSize,
          option3: null,
          available: variantData.available,
          price: variantData.price,
          compare_at_price: variantData.compare_at_price,
          inventory_quantity: variantData.inventory,
          max_pre_orders: variantData.max_pre_orders
        };
        
        variantScript.textContent = JSON.stringify(variant);
      }
    }
    
    updateBISModalTitle(variantData) {
      const modalTitle = document.getElementById('bis-modal-title');
      if (!modalTitle) return;
      
      if (variantData.bis_modal_title) {
        modalTitle.textContent = variantData.bis_modal_title;
      } else {
        // Fallback: build title from current color and size
        const productTitle = document.querySelector('h1.product__title, .product__title h1, h1[data-product-title]');
        const productName = productTitle ? productTitle.textContent.trim() : 'Product';
        
        const currentColor = this.currentColor || variantData.option1 || '';
        const currentSize = this.currentSize || variantData.option2 || '';
        
        let titleText = productName;
        if (currentColor && currentColor !== 'Default Title') {
          titleText += ` — ${currentColor}`;
        }
        if (currentSize && currentSize !== 'Default Title') {
          titleText += ` / ${currentSize}`;
        }
        
        modalTitle.textContent = titleText;
      }
    }
    
    updateProductStickyBar(variantData) {
      //console.log('📌 Updating product sticky bar...');
      
      // Find the sticky bar
      const stickyBar = document.querySelector('.product-sticky-bar');
      if (!stickyBar) {
        //console.log('   ℹ️ No sticky bar found');
        return;
      }
      
      // Update sticky bar image
      if (variantData.featured_media && variantData.featured_media.src) {
        const stickyImage = stickyBar.querySelector('img');
        if (stickyImage) {
          stickyImage.src = variantData.featured_media.src;
          if (variantData.featured_media.srcset) {
            stickyImage.srcset = variantData.featured_media.srcset;
          }
          if (variantData.featured_media.alt) {
            stickyImage.alt = variantData.featured_media.alt;
          }
          //console.log('   ✅ Updated sticky bar image');
        }
      }
      
      // Update sticky bar title
      const stickyTitle = stickyBar.querySelector('.h6, .product-title, h6');
      if (stickyTitle) {
        const newTitle = variantData.product_title || variantData.title || 'Product';
        stickyTitle.textContent = newTitle;
        //console.log('   ✅ Updated sticky bar title');
      }
      
      // Update sticky bar price
	  console.log('💰 Updating sticky bar price...');
      console.log('📊 Sticky variantData:', variantData);
      const stickyHStack = stickyBar.querySelector('.product-sticky-bar__info .h-stack');
      if (stickyHStack) {
        console.log('✅ Found sticky h-stack, updating price...');
        this.renderPriceList(stickyHStack, variantData);
      } else {
        console.log('❌ Sticky h-stack not found');
      }
      
      // Clear existing badges from sticky bar
      const stickyBadges = stickyBar.querySelectorAll('.badge, .badge--on-sale, .badge--custom, .badge--sold-out, sold-out-badge, [class*="badge"]');
      stickyBadges.forEach(badge => badge.remove());
      if (stickyBadges.length > 0) {
        //console.log(`   🧹 Cleared ${stickyBadges.length} badges from sticky bar`);
      }
      
      // Update sticky bar button using the same button state
      const stickyButton = stickyBar.querySelector('button[type="button"], button[type="submit"]');
      if (stickyButton) {
        // Use the same button state logic as main buttons
        let buttonText = 'Select size';
        let buttonDisabled = true;
        
        switch (this.buttonState) {
          case BUTTON_STATES.SELECT_SIZE:
            buttonText = 'Select size';
            buttonDisabled = true;
            break;
          case BUTTON_STATES.ADD_TO_CART:
            buttonText = 'Add to cart';
            buttonDisabled = false;
            break;
          case BUTTON_STATES.PRE_ORDER:
            buttonText = 'Pre-order';
            buttonDisabled = false;
            break;
          case BUTTON_STATES.NOTIFY_ME:
            buttonText = 'NOTIFY ME WHEN AVAILABLE';
            buttonDisabled = false;
            break;
        }
        
        // Update button properties
        stickyButton.textContent = buttonText;
        stickyButton.disabled = buttonDisabled;
        stickyButton.classList.remove('button--outline');
        
        // Handle CSS classes for button styling
        if (this.buttonState === BUTTON_STATES.SELECT_SIZE) {
          // For sticky "Select size" button - keep it enabled but styled as disabled
          stickyButton.classList.add('select-size-disabled');
          stickyButton.classList.remove('disabled'); // Keep enabled for clicking
        } else if (buttonDisabled) {
          // Button is disabled - add gray styling classes
          stickyButton.classList.add('select-size-disabled');
          stickyButton.classList.add('disabled');
        } else {
          // Button is enabled - remove gray styling classes
          stickyButton.classList.remove('select-size-disabled');
          stickyButton.classList.remove('disabled');
        }
        
        // Handle BIS button conversion
        if (this.buttonState === BUTTON_STATES.NOTIFY_ME) {
          if (!stickyButton.classList.contains('my-bis-button')) {
            stickyButton.classList.add('my-bis-button');
          }
          stickyButton.type = 'button';
          stickyButton.setAttribute('data-product-title', this.currentColor || 'Product');
          stickyButton.setAttribute('data-variant-id', variantData.variant_id);
          stickyButton.setAttribute('data-variant-title', `${this.currentColor} / ${this.currentSize}`);
          
          // Re-wire BIS events if needed
          if (window.wireNotifyButtons) {
            window.wireNotifyButtons();
          }
        } else if (this.buttonState === BUTTON_STATES.SELECT_SIZE) {
          // Handle "Select size" button - scroll to product options
          console.log('🔘 Setting up SELECT_SIZE sticky button with scroll listener');
          stickyButton.type = 'button';
          stickyButton.classList.remove('my-bis-button');
          stickyButton.removeAttribute('data-product-title');
          stickyButton.removeAttribute('data-variant-id');
          stickyButton.removeAttribute('data-variant-title');

          // Remove any existing click listeners to prevent duplicates
          const newStickyButton = stickyButton.cloneNode(true);
          stickyButton.parentNode.replaceChild(newStickyButton, stickyButton);

          // Add click event listener to scroll to product options
          newStickyButton.addEventListener('click', (e) => {
            e.preventDefault();
            console.log('📏 Sticky "Select size" button clicked - scrolling to buy-buttons...');

            // Find the buy-buttons section
            const buyButtons = document.querySelector('[data-block-id="buy_buttons"] .buy-buttons');
            if (buyButtons) {
              // Get the document-absolute position of buy-buttons
              const buyButtonsRect = buyButtons.getBoundingClientRect();
              const viewportHeight = window.innerHeight;
              const buttonHeight = buyButtonsRect.height;

              // Current scroll position in document
              const currentScrollY = window.scrollY;

              // Document position of buy-buttons top
              const buyButtonsDocumentTop = currentScrollY + buyButtonsRect.top;

              // Where we want the buy-buttons bottom to be: 50px from viewport bottom
              // This means: viewport height - button height - 50px = where button top should be in viewport
              const targetViewportTop = viewportHeight - buttonHeight - 50;

              // So in document coordinates: we want buy-buttons top at (scrollY + targetViewportTop)
              // Solving: scrollY_new + 0 = buyButtonsDocumentTop - targetViewportTop
              const targetScrollY = buyButtonsDocumentTop - targetViewportTop;

              console.log(`📏 Scroll calc: currentScrollY=${currentScrollY}, buyButtonsDocumentTop=${buyButtonsDocumentTop}, viewportHeight=${viewportHeight}, buttonHeight=${buttonHeight}, targetViewportTop=${targetViewportTop}, targetScrollY=${targetScrollY}, scrollDistance=${currentScrollY - targetScrollY}`);

              // Scroll to the calculated position with smooth animation
              window.scrollTo({
                top: targetScrollY,
                behavior: 'smooth'
              });
              console.log('✅ Scrolling to buy-buttons (50px from bottom)');
            } else {
              console.warn('⚠️ Buy-buttons section not found');
            }
          });
        } else {
          stickyButton.type = 'submit';
          stickyButton.classList.remove('my-bis-button');
          stickyButton.removeAttribute('data-product-title');
          stickyButton.removeAttribute('data-variant-id');
          stickyButton.removeAttribute('data-variant-title');
        }
        
        //console.log(`   ✅ Updated sticky bar button: ${buttonText} (disabled: ${buttonDisabled})`);
      }
    }
    
    formatMoney(amount) {
      // Shopify stores prices in cents, so we need to divide by 100
      if (typeof amount === 'number') {
        const dollars = amount / 100;
        return `$${dollars.toFixed(2)}`;
      }
      return amount || '';
    }
    
    updateForm(variantId) {
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
      }
      
      // Update pre-order properties if this is a pre-order variant
      this.updatePreOrderProperties(variantId);
    }
    
    updatePreOrderProperties(variantId) {
      // Find the variant data for this variant ID
      const variantData = this.findVariantDataById(variantId);
      if (!variantData) return;
      
      // Check if this is a pre-order variant
      const inventory = variantData.inventory || 0;
      const maxPreOrders = variantData.max_pre_orders || 0;
      const isPreOrder = inventory <= 0 && inventory > maxPreOrders;
      
      // Find or create the pre-order property input
      let preOrderInput = document.querySelector('input[name="properties[Pre-order]"]');
      
      if (isPreOrder) {
        // This is a pre-order variant
        const dispatchDate = variantData.dispatch_date || 'TBD';
        
        if (!preOrderInput) {
          // Create the input if it doesn't exist
          // Try multiple form selectors to find the correct form
          const form = document.querySelector('form[data-product-form]') || 
                      document.querySelector('form[id*="product-form"]') ||
                      document.querySelector('form[action*="/cart/add"]') ||
                      document.querySelector('form');
          
          if (form) {
            preOrderInput = document.createElement('input');
            preOrderInput.type = 'hidden';
            preOrderInput.name = 'properties[Pre-order]';
            form.appendChild(preOrderInput);
            console.log('✅ Added pre-order property input to form:', form.id || form.className);
          } else {
            console.warn('❌ Could not find product form to add pre-order property');
          }
        }
        
        if (preOrderInput) {
          if (dispatchDate !== 'TBD') {
            // Format the date as "**Pre-Order: Ships Aug. 16**"
            const date = new Date(dispatchDate);
            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const day = date.getDate();
            preOrderInput.value = `**Pre-Order: Ships ${month}. ${day}`;
            console.log('✅ Set pre-order property value:', preOrderInput.value);
          } else {
            preOrderInput.value = '**Pre-Order: Ships TBD';
            console.log('✅ Set pre-order property value: **Pre-Order: Ships TBD');
          }
        }
      } else {
        // This is not a pre-order variant, remove the property if it exists
        if (preOrderInput) {
          preOrderInput.remove();
          console.log('✅ Removed pre-order property (not a pre-order variant)');
        }
      }
    }
    
    findVariantDataById(variantId) {
      if (!this.variantMapping) return null;
      
      for (const color in this.variantMapping) {
        for (const size in this.variantMapping[color]) {
          if (this.variantMapping[color][size].variant_id == variantId) {
            return this.variantMapping[color][size];
          }
        }
      }
      return null;
    }
    
    triggerThemeEvents(variantData) {
      const variant = {
        id: variantData.variant_id,
        available: variantData.available,
        price: variantData.price,
        compare_at_price: variantData.compare_at_price,
        inventory_quantity: variantData.inventory,
        max_pre_orders: variantData.max_pre_orders
      };
      
      // Trigger theme variant change event
      const form = document.querySelector('form[data-product-form]');
      if (form) {
        const event = new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            formId: form.id,
            variant: variant,
            previousVariant: null
          }
        });
        form.dispatchEvent(event);
      }
      
      // Also trigger a custom event for our own components
      const customEvent = new CustomEvent('split-product:variant-changed', {
        bubbles: true,
        detail: { variant, variantData }
      });
      document.dispatchEvent(customEvent);
    }
    
    updateColorNamesInUI() {
      // Debounce to prevent multiple rapid calls
      if (this.updateColorNamesTimeout) {
        clearTimeout(this.updateColorNamesTimeout);
      }
      
      this.updateColorNamesTimeout = setTimeout(() => {
        this._updateColorNamesInUI();
      }, 50);
    }
    
    _updateColorNamesInUI() {
      if (!this.variantMapping || !this.currentColor) return;
      
      // Find the currently selected color swatch
      const selectedSwatch = document.querySelector(`.ajax-color-swatch[data-color="${this.currentColor}"].is-selected`);
      if (!selectedSwatch) return;
      
      // Find the parent container
      const parentContainer = selectedSwatch.closest('.core-colors, .colors-row');
      if (!parentContainer) return;
      
      // Get all color section headers
      const colorHeaders = document.querySelectorAll('.variant-picker .text-subdued');
      
      colorHeaders.forEach(header => {
        const headerText = header.textContent.trim();
        
        // Check if this is a color header (contains "Colors")
        if (headerText.includes('Colors')) {
          // Check if this header belongs to the selected color's parent container
          const headerParent = header.closest('.core-colors, .colors-row');
          const isSelectedSection = headerParent === parentContainer;
          
          // Update the header text
          if (isSelectedSection) {
            // This is the selected color section - show color name
            const baseText = headerText.split(':')[0];
            header.textContent = `${baseText}: ${this.currentColor}`;
          } else {
            // This is not the selected color section - remove color name
            const baseText = headerText.split(':')[0];
            header.textContent = baseText;
          }
        }
      });
    }
  }
  
  // Tell the theme this is a split product
  window.hasSplitProductVariantPicker = true;
  
  // Initialize when DOM is ready
  let splitProductManager;
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      splitProductManager = new SplitProductManager();
      // Expose the instance globally for debugging
      window.SplitProductManager = { 
        instance: splitProductManager,
        inspectVariantMapping: () => {
          if (splitProductManager && splitProductManager.variantMapping) {
            console.log('🔍 VARIANT MAPPING INSPECTION:');
            console.log('📊 Total colors:', Object.keys(splitProductManager.variantMapping).length);
            Object.entries(splitProductManager.variantMapping).forEach(([color, sizes]) => {
              console.log(`🎨 Color: ${color} - Sizes: ${Object.keys(sizes).join(', ')}`);
              Object.entries(sizes).forEach(([size, data]) => {
                console.log(`   📏 ${size}: Variant ID ${data.variant_id}, Available: ${data.available}, Price: ${data.price}`);
              });
            });
          } else {
            console.log('❌ No variant mapping available');
          }
        }
      };
    });
  } else {
    splitProductManager = new SplitProductManager();
    // Expose the instance globally for debugging
    window.SplitProductManager = { 
      instance: splitProductManager,
      inspectVariantMapping: () => {
        if (splitProductManager && splitProductManager.variantMapping) {
          console.log('🔍 VARIANT MAPPING INSPECTION:');
          console.log('📊 Total colors:', Object.keys(splitProductManager.variantMapping).length);
          Object.entries(splitProductManager.variantMapping).forEach(([color, sizes]) => {
            console.log(`🎨 Color: ${color} - Sizes: ${Object.keys(sizes).join(', ')}`);
            Object.entries(sizes).forEach(([size, data]) => {
              console.log(`   📏 ${size}: Variant ID ${data.variant_id}, Available: ${data.available}, Price: ${data.price}`);
            });
          });
        } else {
          console.log('❌ No variant mapping available');
        }
      }
    };
  }
  
})(); 
